/**
 * One bar as the builder's bar editor edits it: a run of slices that always adds up to the bar.
 *
 *   [{ chord: 'C', beats: 3 }, { chord: 'G', beats: 1 }]
 *
 * `chord: null` is a tie — the chord from before the bar still ringing — `rest` marks silence, and
 * `missing` marks beats nobody has written yet. Every edit here keeps the total, so a bar can't be
 * left short or run over.
 */

import { BEAT_EPSILON, cellChordBeats, formatCell, setCellMelody, splitBeats, subdivisionFor } from './chordBars';

const barLength = (beatsPerBar) => (Number(beatsPerBar) > 0 ? Number(beatsPerBar) : 4);

const sum = (values) => values.reduce((total, value) => total + value, 0);

const onStep = (beats, step) => Math.abs(beats / step - Math.round(beats / step)) < BEAT_EPSILON;

/** The finest a slice can be cut: a quarter beat, or a half where the beat is already an eighth note. */
export const barStep = (timeSignature) => (String(timeSignature ?? '').split('/')[1] === '8' ? 0.5 : 0.25);

/**
 * A cell as slices. An empty cell is one tie across the bar; a bar left short gives the rest to its
 * last slice, as gridToChartData reads it.
 */
export const readBarSlices = (cell, beatsPerBar) => {
  const bar = barLength(beatsPerBar);
  const entries = cellChordBeats(cell, bar);

  if (entries.length === 0) return [{ chord: null, beats: bar }];

  const slices = entries.map(({ chord, beats, missing, rest }) => ({
    chord,
    beats: beats ?? bar,
    ...(missing ? { missing: true } : null),
    ...(rest ? { rest: true } : null),
  }));
  const short = bar - sum(slices.map(({ beats }) => beats));

  if (short > BEAT_EPSILON) slices[slices.length - 1].beats += short;

  return slices;
};

/**
 * Slices written back as cell text: bare chords wherever they share the bar the way an unmarked bar
 * is read (splitBeats), beat marks only where the bar says something bare chords can't. `melody`
 * marks it as a melody bar.
 */
export const sliceCell = (slices, beatsPerBar, { melody = false } = {}) => {
  const bar = barLength(beatsPerBar);

  if (slices.every(({ chord, missing, rest }) => chord === null && !missing && !rest)) return '';

  if (slices.every(({ chord }) => chord !== null)) {
    if (slices.length === 1) return setCellMelody(slices[0].chord, melody);

    const even = splitBeats(slices.length, bar);

    if (slices.every(({ beats }, i) => Math.abs(beats - even[i]) < BEAT_EPSILON)) {
      return setCellMelody(slices.map(({ chord }) => chord).join(' '), melody);
    }
  }

  const grid = subdivisionFor(slices.map(({ beats }) => beats));
  const text = formatCell(
    slices.map(({ chord, missing, rest, beats }) => ({
      chord,
      slots: Math.max(1, Math.round(beats * grid)),
      ...(missing ? { missing } : null),
      ...(rest ? { rest } : null),
    })),
    bar,
  );

  return setCellMelody(text, melody);
};

/** The grid with one cell's text replaced. */
export const replaceCell = (lines, { line, bar }, text) =>
  lines.map((cells, l) => (l === line ? cells.map((cell, b) => (b === bar ? text : cell)) : cells));

/** Where each slice starts, in beats from the bar line. */
export const sliceStarts = (slices) =>
  slices.reduce((starts, { beats }, i) => [...starts, starts[i] + beats], [0]).slice(0, slices.length);

/**
 * The line between slice `divider` and the one after it, moved to `at` beats into the bar. Only
 * those two change: what one gains the other gives up, and neither goes below a step.
 */
export const placeDivider = (slices, divider, at, step) => {
  const left = slices[divider];
  const right = slices[divider + 1];

  if (!left || !right) return slices;

  const start = sliceStarts(slices)[divider];
  const both = left.beats + right.beats;

  if (both < 2 * step - BEAT_EPSILON) return slices;

  const snapped = Math.round((at - start) / step) * step;
  const beats = Math.min(both - step, Math.max(step, snapped));

  if (Math.abs(beats - left.beats) < BEAT_EPSILON) return slices;

  return slices.map((slice, i) => {
    if (i === divider) return { ...slice, beats };
    if (i === divider + 1) return { ...slice, beats: both - beats };

    return slice;
  });
};

/**
 * A slice made `delta` beats longer (or shorter) at the expense of its neighbour: the one after it,
 * or the one before when it is the last. A slice on its own has nothing to trade with.
 */
export const resizeSlice = (slices, index, delta, step) => {
  const slice = slices[index];

  if (!slice || slices.length < 2) return slices;

  const starts = sliceStarts(slices);

  return index < slices.length - 1
    ? placeDivider(slices, index, starts[index] + slice.beats + delta, step)
    : placeDivider(slices, index - 1, starts[index] - delta, step);
};

/** Whether resizeSlice would change anything, for the − / + it is behind. */
export const canResizeSlice = (slices, index, delta, step) => resizeSlice(slices, index, delta, step) !== slices;

/** Whether a slice halves onto the step grid. */
export const canSplitSlice = (slices, index, step) => {
  const beats = slices[index]?.beats;

  return beats >= 2 * step - BEAT_EPSILON && onStep(beats / 2, step);
};

/** A slice cut into two halves of itself; the second half is the one selected afterwards. */
export const splitSlice = (slices, index, step) => {
  if (!canSplitSlice(slices, index, step)) return { slices, selected: index };

  const half = { ...slices[index], beats: slices[index].beats / 2 };

  return { slices: [...slices.slice(0, index), half, { ...half }, ...slices.slice(index + 1)], selected: index + 1 };
};

/**
 * A slice taken out, its beats going to the slice before it (after it, when it is the first). The
 * last one left makes the bar a hold.
 */
export const removeSlice = (slices, index) => {
  if (slices.length <= 1) return { slices: [{ chord: null, beats: sum(slices.map(({ beats }) => beats)) }], selected: 0 };

  const into = index > 0 ? index - 1 : 1;
  const next = slices.map((slice, i) => (i === into ? { ...slice, beats: slice.beats + slices[index].beats } : slice));

  next.splice(index, 1);

  return { slices: next, selected: index > 0 ? index - 1 : 0 };
};

/**
 * "Hold" on a slice: the first one becomes a tie to whatever was ringing before the bar; any later
 * one is taken back by the chord before it.
 */
export const holdSlice = (slices, index) => {
  if (index > 0) return removeSlice(slices, index);

  const { missing, rest, ...slice } = slices[0];

  // A tie followed by another tie is the same tie, so the two become one
  const tied = [{ ...slice, chord: null }, ...slices.slice(1)];

  return tied.length > 1 && tied[1].chord === null && !tied[1].missing && !tied[1].rest
    ? { slices: [{ chord: null, beats: tied[0].beats + tied[1].beats }, ...tied.slice(2)], selected: 0 }
    : { slices: tied, selected: 0 };
};

/** A slice given a chord — which is how a tie or a stretch of missing beats gets one. */
export const setSliceChord = (slices, index, chord) =>
  slices.map((slice, i) => (i === index ? { chord, beats: slice.beats } : slice));

/** The next slice still missing its chord after `from`, wrapping round the bar; `from` when there is none. */
export const nextMissing = (slices, from) => {
  const order = [...slices.keys()].map((i) => (from + 1 + i) % slices.length);

  return order.find((i) => slices[i].missing) ?? from;
};

/**
 * "Rest" on a slice: silence until the next chord. A rest beside another one is the same silence,
 * so the two become one.
 */
export const restSlice = (slices, index) => {
  const rested = slices.map((slice, i) => (i === index ? { chord: null, rest: true, beats: slice.beats } : slice));
  const merged = [];
  let selected = index;

  rested.forEach((slice, i) => {
    const last = merged[merged.length - 1];

    if (slice.rest && last?.rest) {
      last.beats += slice.beats;
      if (i <= index) selected -= 1;
    } else {
      merged.push({ ...slice });
    }
  });

  return { slices: merged, selected };
};

/**
 * Where a chart that sounds over the count-in comes in: the beats of the rest its first bar opens on.
 * 0 when it opens on sound — or on nothing but rest, which leaves no entry in the bar to point at.
 */
export const entryBeats = (slices) => (slices.length > 1 && slices[0].rest ? slices[0].beats : 0);

/**
 * The first bar made to come in `beats` into the count-in: those beats a rest, and whatever sounds at
 * that point struck there. Chords wholly before it are dropped. A rest it already opened on is the
 * old entry, so it is taken out first — its beats go to what came after it.
 */
export const setEntry = (slices, beats) => {
  let bar = slices;

  if (entryBeats(bar) > 0) bar = [{ ...bar[1], beats: bar[0].beats + bar[1].beats }, ...bar.slice(2)];

  if (!(beats > BEAT_EPSILON)) return bar;

  const starts = sliceStarts(bar);
  const kept = bar.flatMap((slice, i) => {
    const end = starts[i] + slice.beats;

    if (end <= beats + BEAT_EPSILON) return [];

    return [{ ...slice, beats: end - Math.max(starts[i], beats) }];
  });

  // Silence already sounding from the entry on is the same silence as the count-in's
  if (kept[0]?.rest) return [{ ...kept[0], beats: kept[0].beats + beats }, ...kept.slice(1)];

  return [{ chord: null, rest: true, beats }, ...kept];
};

/**
 * Taps moved so the first lands `shift` beats into the bar. The click sets the tempo and the spacing,
 * but not where the rhythm starts: sound reaches the ear late — a third of a second through
 * Bluetooth — and by how much changes from one device and one run to the next, where the gaps
 * between taps don't. Taps from the count-in, more than half a beat before its end, are dropped.
 */
export const alignTaps = (taps, shift = 0) => {
  const played = taps.filter((at) => at >= -0.5);

  return played.map((at) => at - played[0] + shift);
};

/**
 * A tapped rhythm as slices, bar by bar. `taps` are in beats from the first bar's downbeat, one per
 * chord change, and `bars` are the slices of the bars they run across. The chords of the bars tapped
 * through are one run, handed out in order, so a note can move over a bar line; taps past them are
 * left missing, and a bar whose first tap is late opens on a tie. A bar with no taps between two
 * that have them is held through; one before the first tap or after the last is left alone (null).
 * `extra` chords carry the run on past the bars' own, into what would otherwise be missing; a null
 * one leaves its tap missing. With `markPicks`, each slice past the bars' own carries `pick`, its
 * index into `extra`.
 */
export const slicesFromTaps = (taps, { bars, beatsPerBar, step, extra = [], markPicks = false }) => {
  const bar = barLength(beatsPerBar);
  // Snapped to the step, so an early downbeat within half a step comes out at 0
  const points = [...new Set(taps.map((at) => Math.round(at / step) * step + 0))]
    .filter((at) => at >= 0 && at < bars.length * bar - BEAT_EPSILON)
    .sort((a, b) => a - b);
  const barOf = (at) => Math.floor((at + BEAT_EPSILON) / bar);

  if (points.length === 0) return bars.map(() => null);

  const first = barOf(points[0]);
  const last = barOf(points[points.length - 1]);
  const own = bars
    .slice(first, last + 1)
    .flat()
    .filter(({ chord }) => chord !== null)
    .map(({ chord }) => chord);
  const chords = own.concat(extra);
  let next = 0;

  return bars.map((_, index) => {
    if (index < first || index > last) return null;

    const starts = points.filter((at) => barOf(at) === index).map((at) => at - index * bar);

    if (starts.length === 0) return [{ chord: null, beats: bar }];

    const tie = starts[0] > BEAT_EPSILON ? [{ chord: null, beats: starts[0] }] : [];

    return [
      ...tie,
      ...starts.map((at, i) => {
        const beats = (starts[i + 1] ?? bar) - at;
        const run = next++;
        const chord = chords[run];
        const pick = markPicks && run >= own.length ? { pick: run - own.length } : null;

        return chord != null ? { chord, beats, ...pick } : { chord: null, missing: true, beats, ...pick };
      }),
    ];
  });
};

// A chord written into a tapped result's slot `at`, leaving any slots skipped on the way open
export const placePick = (picks, at, chord) => {
  const next = [...picks];

  next[at] = chord;

  return Array.from(next, (pick) => pick ?? null);
};
